/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useDispatch } from 'react-redux';
import logger from './logger';
import {
  setHistoryMsg,
  setInterruptMsg,
  updateAITalkState,
  updateAIThinkState,
  addAssessmentRecord,
} from '@/store/slices/room';
import RtcClient from '@/lib/RtcClient';
import { string2tlv, tlv2String } from '@/utils/utils';

export type AnyRecord = Record<string, any>;

export enum MESSAGE_TYPE {
  BRIEF = 'conv',
  SUBTITLE = 'subv',
  FUNCTION_CALL = 'tool',
}

export enum AGENT_BRIEF {
  UNKNOWN,
  LISTENING,
  THINKING,
  SPEAKING,
  INTERRUPTED,
  FINISHED,
}

/**
 * @brief 指令类型
 */
export enum COMMAND {
  /**
   * @brief 打断指令
   */
  INTERRUPT = 'interrupt',
  /**
   * @brief 发送外部文本驱动 TTS
   */
  EXTERNAL_TEXT_TO_SPEECH = 'ExternalTextToSpeech',
  /**
   * @brief 发送外部文本驱动 LLM
   */
  EXTERNAL_TEXT_TO_LLM = 'ExternalTextToLLM',
}
/**
 * @brief 打断的类型
 */
export enum INTERRUPT_PRIORITY {
  /**
   * @brief 占位
   */
  NONE,
  /**
   * @brief 高优先级。传入信息直接打断交互，进行处理。
   */
  HIGH,
  /**
   * @brief 中优先级。等待当前交互结束后，进行处理。
   */
  MEDIUM,
  /**
   * @brief 低优先级。如当前正在发生交互，直接丢弃 Message 传入的信息。
   */
  LOW,
}

export const MessageTypeCode = {
  [MESSAGE_TYPE.SUBTITLE]: 1,
  [MESSAGE_TYPE.FUNCTION_CALL]: 2,
  [MESSAGE_TYPE.BRIEF]: 3,
};

export const useMessageHandler = () => {
  const dispatch = useDispatch();

  const maps = {
    /**
     * @brief 接收状态变化信息
     * @note https://www.volcengine.com/docs/6348/1415216?s=g
     */
    [MESSAGE_TYPE.BRIEF]: (parsed: AnyRecord) => {
      const { Stage } = parsed || {};
      const { Code, Description } = Stage || {};
      logger.debug('[MESSAGE_TYPE.BRIEF]: ', Code, Description);
      switch (Code) {
        case AGENT_BRIEF.THINKING:
          dispatch(updateAIThinkState({ isAIThinking: true }));
          break;
        case AGENT_BRIEF.SPEAKING:
          dispatch(updateAITalkState({ isAITalking: true }));
          break;
        case AGENT_BRIEF.FINISHED:
          dispatch(updateAITalkState({ isAITalking: false }));
          break;
        case AGENT_BRIEF.INTERRUPTED:
          dispatch(setInterruptMsg());
          break;
        default:
          break;
      }
    },
    /**
     * @brief 字幕
     * @note https://www.volcengine.com/docs/6348/1337284?s=g
     */
    [MESSAGE_TYPE.SUBTITLE]: (parsed: AnyRecord) => {
      const data = parsed.data?.[0] || {};
      /** debounce 记录用户输入文字 */
      if (data) {
        console.log('《〈《〈《〈 **** [输出聊天内容 data ] **** 》〉》〉》〉》', data);
        const { text: msg } = data;
        const { definite, userId: user, paragraph } = data;
        const isAudioEnable = RtcClient.getAgentEnabled();
        if ((window as any)._debug_mode) {
          logger.debug('handleRoomBinaryMessageReceived', data);
        }

        // 提取动作名称并暂存（用于后续与分数组合）
        if (msg && definite) {
          const actionMatch = msg.match(/(?:该患者|患者)完成了(.+?)的动作/);
          if (actionMatch && actionMatch[1]) {
            // 暂存最新的动作名称到全局变量
            if (!(window as any)._lastActionName) {
              (window as any)._lastActionName = '';
            }
            (window as any)._lastActionName = actionMatch[1].trim();
            console.log(
              '《〈《〈《〈 **** [暂存动作名称] **** 》〉》〉》〉》',
              (window as any)._lastActionName
            );
          }

          // 提取 Berg 项目序号并暂存：支持多种格式
          // "第 1 项（共 14 项）" 或 "当前为第1项（共14项）"
          const bergIdRegex = /(?:当前为|为)?第\s*(\d+)\s*项(?:\s*[（(]\s*共\s*\d+\s*项\s*[）)])?/;
          const bergIdMatch = msg?.match(bergIdRegex);
          if (bergIdMatch && bergIdMatch[1]) {
            // 暂存 Berg ID 到全局变量
            if (!(window as any)._lastBergId) {
              (window as any)._lastBergId = '';
            }
            (window as any)._lastBergId = bergIdMatch[1];
            console.log(
              '《〈《〈《〈 **** [暂存 Berg 项目序号] **** 》〉》〉》〉》',
              (window as any)._lastBergId
            );
          }
        }

        // 解析并提取评估分数：从"评估分数：XX分"格式中提取
        const scoreRegex = /评估分数[：:]\s*(\d+)\s*分/;
        const scoreMatch = msg?.match(scoreRegex);

        // 如果检测到分数且是完整消息，提取并存储评估记录
        if (msg && scoreMatch && definite) {
          const score = parseInt(scoreMatch[1], 10);

          // 使用之前暂存的动作名称
          let action = (window as any)._lastActionName || '动作评估';

          // 如果动作描述太长，截取前50个字符
          if (action.length > 50) {
            action = `${action.substring(0, 50)}...`;
          }

          // 使用之前暂存的 Berg ID
          const bergId = (window as any)._lastBergId || undefined;

          // 存储评估记录
          dispatch(
            addAssessmentRecord({
              id: bergId,
              action,
              score,
              timestamp: new Date().toISOString(),
            })
          );

          console.log('《〈《〈《〈 **** [检测到评估分数] **** 》〉》〉》〉》', {
            id: bergId,
            action,
            score,
            fullMsg: msg,
          });

          // 清空暂存的动作名称和 Berg ID
          (window as any)._lastActionName = '';
          (window as any)._lastBergId = '';
        }

        if (isAudioEnable && msg) {
          dispatch(setHistoryMsg({ text: msg, user, paragraph, definite }));
        }
      }
    },
    /**
     * @brief Function calling
     * @note https://www.volcengine.com/docs/6348/1359441?s=g
     */
    [MESSAGE_TYPE.FUNCTION_CALL]: (parsed: AnyRecord) => {
      const name: string = parsed?.tool_calls?.[0]?.function?.name;
      console.log('[Function Call] - Called by sendUserBinaryMessage');
      const map: Record<string, string> = {
        getcurrentweather: '今天下雪， 最低气温零下10度',
      };

      RtcClient.engine.sendUserBinaryMessage(
        'RobotMan_',
        string2tlv(
          JSON.stringify({
            ToolCallID: parsed?.tool_calls?.[0]?.id,
            Content: map[name.toLocaleLowerCase().replaceAll('_', '')],
          }),
          'func'
        )
      );
    },
  };

  return {
    parser: (buffer: ArrayBuffer) => {
      try {
        const { type, value } = tlv2String(buffer);
        console.log('《〈《〈《〈 **** [ 解析字节流：类型 ] **** 》〉》〉》〉》', type);
        console.log('《〈《〈《〈 **** >>>>[ 解析字节流：值 ]<<<<< **** 》〉》〉》〉》', value);
        maps[type as MESSAGE_TYPE]?.(JSON.parse(value));
      } catch (e) {
        logger.debug('parse error', e);
      }
    },
  };
};
